import { supabase } from '@/integrations/supabase/client';

/**
 * Wrapper de `supabase.functions.invoke` que extrai o MOTIVO REAL do erro
 * quando a edge function retorna HTTP 4xx/5xx.
 *
 * PROBLEMA QUE RESOLVE:
 * O cliente supabase-js converte qualquer non-2xx num FunctionsHttpError
 * com mensagem genérica "Edge Function returned a non-2xx status code".
 * O body da resposta — onde está o motivo de verdade — fica enterrado em
 * `error.context` e o código padrão `if (error) throw error` o descarta.
 *
 * O `solicitar-fatura` (linhas 665-683 do index.ts) JÁ tenta retornar 200
 * com `ok:false` justamente pra evitar isso. Mas ainda há 9 pontos no
 * caminho (validação, JWT, dedup, webhook, exception) que retornam non-2xx.
 * Esse helper cobre esses casos.
 *
 * Uso:
 *   const { data, error, mensagem } = await invokeWithRealError(
 *     'solicitar-fatura',
 *     { body: { cliente_id, linha_plano, linha_solicitante } }
 *   );
 *   if (error) throw new Error(mensagem);
 *   if (data?.ok === false) throw new Error(data.mensagem || data.error);
 *
 * Retorno:
 *   - data: corpo JSON quando a edge function retornou 2xx
 *   - error: o erro original do supabase-js (null se sucesso)
 *   - mensagem: motivo real extraído do body (sempre legível, nunca
 *     "non-2xx status code" — fallback pra error.message se body vazio)
 */
export async function invokeWithRealError<T = any>(
  functionName: string,
  options?: { body?: any; headers?: Record<string, string> },
): Promise<{ data: T | null; error: any; mensagem: string | null }> {
  const { data, error } = await supabase.functions.invoke<T>(functionName, options);

  if (!error) {
    return { data, error: null, mensagem: null };
  }

  // Tenta extrair body da resposta — supabase-js v2 expõe via error.context (Response)
  let mensagem: string | null = null;
  try {
    const ctx = (error as any).context;
    if (ctx && typeof ctx.json === 'function') {
      const body = await ctx.json().catch(() => null);
      if (body && typeof body === 'object') {
        mensagem = body.error || body.mensagem || body.message || null;
      }
    } else if (ctx && typeof ctx.text === 'function') {
      const txt = await ctx.text().catch(() => '');
      if (txt) {
        // Pode ser JSON string OU texto puro
        try {
          const parsed = JSON.parse(txt);
          mensagem = parsed?.error || parsed?.mensagem || parsed?.message || txt.slice(0, 200);
        } catch {
          mensagem = txt.slice(0, 200);
        }
      }
    }
  } catch {
    // Mantém mensagem null pra cair no fallback abaixo
  }

  // Fallback: se body não tinha mensagem útil OU veio "non-2xx status code"
  // genérico, troca por algo mais claro pro operador.
  if (!mensagem || mensagem.includes('non-2xx status code')) {
    mensagem = error?.message?.includes('non-2xx')
      ? 'Servidor recusou a requisição. Recarregue a página (F5) e tente de novo. Se persistir, sessão pode ter expirado — faça logout/login.'
      : (error?.message || 'Erro desconhecido');
  }

  return { data: null, error, mensagem };
}
